/**
 * WebAuthn / Passkeys client — full passkey-first authentication.
 *
 * Uses the browser's platform authenticator (Touch ID / Face ID /
 * Windows Hello / security keys). Biometric data NEVER touches VAULT —
 * only the cryptographic credential ID + public key round-trip.
 */
import { api } from "@/lib/api";

const RP_ID = typeof window !== "undefined" ? window.location.hostname : "";
const RP_NAME = "VAULT by PD";

/* base64url ↔ ArrayBuffer helpers */
export function b64uToBuf(b64u) {
    const b64 = (b64u || "").replace(/-/g, "+").replace(/_/g, "/");
    const pad = b64.length % 4 ? "=".repeat(4 - (b64.length % 4)) : "";
    const bin = atob(b64 + pad);
    const buf = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
    return buf.buffer;
}
export function bufToB64u(buf) {
    if (!buf) return null;
    const bin = String.fromCharCode(...new Uint8Array(buf));
    return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/* Capability detection */
export function isWebAuthnSupported() {
    return typeof window !== "undefined"
        && window.PublicKeyCredential !== undefined
        && typeof navigator.credentials?.create === "function"
        && typeof navigator.credentials?.get === "function";
}

export async function isPlatformAuthenticatorAvailable() {
    if (!isWebAuthnSupported()) return false;
    try {
        return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch { return false; }
}

export async function isConditionalMediationAvailable() {
    if (!isWebAuthnSupported()) return false;
    try {
        return typeof window.PublicKeyCredential.isConditionalMediationAvailable === "function"
            && await window.PublicKeyCredential.isConditionalMediationAvailable();
    } catch { return false; }
}

export function isLikelyTouchIdDevice() {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent || "";
    const platform = navigator.platform || "";
    return /Mac/i.test(platform) || /Macintosh|Mac OS X/i.test(ua);
}

export function passkeyButtonLabel() {
    return isLikelyTouchIdDevice() ? "Sign in with Touch ID" : "Sign in with Passkey";
}

export function friendlyDeviceLabel() {
    if (typeof navigator === "undefined") return "This device";
    const ua = navigator.userAgent || "";
    const platform = navigator.platform || "";
    if (/iPad/i.test(ua)) return "iPad";
    if (/iPhone/i.test(ua)) return "iPhone";
    if (/Android/i.test(ua)) return "Android device";
    if (/Windows/i.test(platform)) return "Windows PC";
    if (/MacBook Pro/i.test(ua)) return "MacBook Pro";
    if (/MacBook Air/i.test(ua)) return "MacBook Air";
    if (/Mac/i.test(platform)) return "Mac";
    return "This device";
}

export async function isPasskeyFeatureEnabled() {
    try {
        const r = await api.get("/webauthn/capability");
        return !!r.data?.enabled;
    } catch { return false; }
}

/* -------------------------------------------------------------------------
 * Registration
 * -----------------------------------------------------------------------*/
export async function registerPasskeyForCurrentUser({ deviceLabel } = {}) {
    if (!isWebAuthnSupported()) throw new Error("WebAuthn is not supported on this device.");
    const beg = await api.post("/webauthn/register-begin", {});
    const d = beg.data;
    const exclude = (d.exclude_credentials || []).map((c) => ({
        type: "public-key", id: b64uToBuf(c),
    }));
    const cred = await navigator.credentials.create({
        publicKey: {
            challenge: b64uToBuf(d.challenge),
            rp: { id: RP_ID, name: RP_NAME },
            user: {
                id: new TextEncoder().encode(d.user.id),
                name: d.user.name,
                displayName: d.user.display_name || d.user.name,
            },
            pubKeyCredParams: [
                { type: "public-key", alg: -7 },
                { type: "public-key", alg: -257 },
            ],
            authenticatorSelection: {
                userVerification: "required",
                residentKey: "preferred",
            },
            excludeCredentials: exclude,
            attestation: "direct",
            timeout: d.timeout_ms || 60_000,
        },
    });
    if (!cred) throw new Error("No credential returned.");
    const credentialId = bufToB64u(cred.rawId);
    const r = await api.post("/webauthn/register-complete", {
        challenge: d.challenge,
        credential_id: credentialId,
        // Full FIDO attestation payload (backend runs verify_registration_response).
        attestation_object: bufToB64u(cred.response.attestationObject),
        client_data_json:   bufToB64u(cred.response.clientDataJSON),
        origin: window.location.origin,
        // Legacy public_key kept for backwards compatibility.
        public_key: cred.response?.getPublicKey ? bufToB64u(cred.response.getPublicKey()) : null,
        device_label: deviceLabel || friendlyDeviceLabel(),
        transports: cred.response?.getTransports?.() || [],
        authenticator_attachment: cred.authenticatorAttachment,
        user_agent: (navigator.userAgent || "").slice(0, 256),
    });
    return r.data;
}

/* -------------------------------------------------------------------------
 * Passwordless sign-in (supports conditional-UI autofill)
 * -----------------------------------------------------------------------*/
export async function signInWithPasskey({ email, mediation, signal } = {}) {
    if (!isWebAuthnSupported()) throw new Error("WebAuthn not supported on this device.");
    const beg = await api.post("/webauthn/login-begin", { email });
    const d = beg.data;
    const allowCredentials = (d.allow_credentials || []).map((id) => ({
        type: "public-key", id: b64uToBuf(id),
    }));
    const opts = {
        publicKey: {
            challenge: b64uToBuf(d.challenge),
            rpId: RP_ID,
            // For conditional-UI (autofill), Chrome requires an EMPTY
            // allowCredentials so the browser can offer any discoverable
            // credential.
            allowCredentials: mediation === "conditional" ? [] : allowCredentials,
            userVerification: d.user_verification || "required",
            timeout: d.timeout_ms || 60_000,
        },
        mediation,
        signal,
    };
    const assertion = await navigator.credentials.get(opts);
    if (!assertion) throw new Error("Authentication cancelled.");
    const credentialId = bufToB64u(assertion.rawId);
    const resp = assertion.response;
    const r = await api.post("/webauthn/login-complete", {
        challenge: d.challenge,
        credential_id: credentialId,
        authenticator_data: bufToB64u(resp.authenticatorData),
        client_data_json:   bufToB64u(resp.clientDataJSON),
        signature:          bufToB64u(resp.signature),
        user_handle: resp.userHandle ? bufToB64u(resp.userHandle) : null,
        origin: window.location.origin,
        user_agent: (navigator.userAgent || "").slice(0, 256),
    });
    return r.data;
}

/* -------------------------------------------------------------------------
 * Step-up reauthentication (sensitive actions)
 * -----------------------------------------------------------------------*/
export async function reauthenticateWithPasskey() {
    if (!isWebAuthnSupported()) throw new Error("WebAuthn not supported on this device.");
    const beg = await api.post("/webauthn/reauth-begin");
    const d = beg.data;
    const allow = (d.allow_credentials || []).map((id) => ({
        type: "public-key", id: b64uToBuf(id),
    }));
    const assertion = await navigator.credentials.get({
        publicKey: {
            challenge: b64uToBuf(d.challenge),
            rpId: RP_ID,
            allowCredentials: allow,
            userVerification: d.user_verification || "required",
            timeout: d.timeout_ms || 60_000,
        },
    });
    if (!assertion) throw new Error("Reauth cancelled.");
    const credentialId = bufToB64u(assertion.rawId);
    const resp = assertion.response;
    const r = await api.post("/webauthn/reauth-complete", {
        challenge: d.challenge,
        credential_id: credentialId,
        authenticator_data: bufToB64u(resp.authenticatorData),
        client_data_json:   bufToB64u(resp.clientDataJSON),
        signature:          bufToB64u(resp.signature),
        origin: window.location.origin,
    });
    return r.data;
}

export async function unlockBiometric() {
    try { await reauthenticateWithPasskey(); return true; }
    catch { return false; }
}
